import { isTypingOrModalTarget } from '@/lib/demo/keyboardGuard';
import { useEffect, useState, useRef } from 'react';
import { useDemo, type AutoAdvanceSpeed } from '@/contexts/DemoContext';
import { ChevronLeft, ChevronRight, X, Pause, Play, Keyboard, FastForward, SkipForward, List } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useIsMobile } from '@/hooks/use-mobile';
import { toast as sonnerToast } from 'sonner';

const SPEED_LABELS: Record<AutoAdvanceSpeed, string> = {
  slow: '0.5×',
  normal: '1×',
  fast: '2×',
};

const SPEED_ORDER: AutoAdvanceSpeed[] = ['slow', 'normal', 'fast'];

/* Countdown ring around auto-advance button */
function CountdownRing({ durationMs, paused, stepKey }: { durationMs: number; paused: boolean; stepKey: number }) {
  const SIZE = 32;
  const STROKE = 2.5;
  const R = (SIZE - STROKE) / 2;
  const C = 2 * Math.PI * R;

  return (
    <svg
      className="absolute inset-0 -rotate-90 pointer-events-none"
      width={SIZE} height={SIZE}
      viewBox={`0 0 ${SIZE} ${SIZE}`}
    >
      <circle cx={SIZE/2} cy={SIZE/2} r={R} fill="none" stroke="white" strokeOpacity={0.15} strokeWidth={STROKE} />
      <circle
        key={stepKey}
        cx={SIZE/2} cy={SIZE/2} r={R}
        fill="none" stroke="white" strokeWidth={STROKE} strokeLinecap="round"
        strokeDasharray={C}
        strokeDashoffset={0}
        style={{
          animation: paused ? 'none' : `countdown-ring ${durationMs}ms linear forwards`,
        }}
      />
    </svg>
  );
}

export function DemoToolbar() {
  const isMobile = useIsMobile();
  const {
    isDemoActive, currentScenario, currentStepIndex, currentStep,
    totalSteps, isExecutingAction, isPaused,
    isAutoAdvancing, autoAdvanceSpeed, currentIntervalMs,
    nextStep, prevStep, exitDemo, togglePause, jumpToStep,
    toggleAutoAdvance, setAutoAdvanceSpeed, showIntro,
  } = useDemo();

  const [showShortcuts, setShowShortcuts] = useState(false);
  const [showJumpMenu, setShowJumpMenu] = useState(false);
  const jumpMenuRef = useRef<HTMLDivElement>(null);

  const cycleSpeed = () => {
    const idx = SPEED_ORDER.indexOf(autoAdvanceSpeed);
    setAutoAdvanceSpeed(SPEED_ORDER[(idx + 1) % SPEED_ORDER.length]);
  };

  // Close jump menu on outside click
  useEffect(() => {
    if (!showJumpMenu) return;
    const handleClick = (e: MouseEvent) => {
      if (jumpMenuRef.current && !jumpMenuRef.current.contains(e.target as Node)) {
        setShowJumpMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showJumpMenu]);

  // Double-ESC to exit: track first ESC press timestamp
  const lastEscRef = useRef<number>(0);

  // Keyboard shortcuts
  useEffect(() => {
    if (!isDemoActive) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey) return;
      if (isTypingOrModalTarget(e.target)) return;
      if (e.key === ' ' && e.target instanceof Element && e.target.closest('button, a, [role="button"]')) return;

      switch (e.key) {
        case 'ArrowRight':
        case ' ':
          e.preventDefault();
          nextStep();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          prevStep();
          break;
        case 'Escape':
          e.preventDefault();
          if (showJumpMenu) {
            setShowJumpMenu(false);
          } else if (showShortcuts) {
            setShowShortcuts(false);
          } else {
            const now = Date.now();
            if (now - lastEscRef.current < 800) {
              // Double-ESC: exit demo
              lastEscRef.current = 0;
              exitDemo();
            } else {
              // First ESC: show hint
              lastEscRef.current = now;
              sonnerToast.info('Press ESC again to exit demo', { duration: 1500, id: 'esc-hint' });
            }
          }
          break;
        case 'Home':
          e.preventDefault();
          jumpToStep(0);
          break;
        case 'End':
          e.preventDefault();
          jumpToStep(totalSteps - 1);
          break;
        case 'p':
        case 'P':
          e.preventDefault();
          togglePause();
          break;
        case '?':
          e.preventDefault();
          setShowShortcuts(prev => !prev);
          break;
        case 'a':
        case 'A':
          e.preventDefault();
          toggleAutoAdvance();
          break;
        case 's':
        case 'S':
          e.preventDefault();
          cycleSpeed();
          break;
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isDemoActive, nextStep, prevStep, exitDemo, togglePause, jumpToStep, totalSteps, showShortcuts, showJumpMenu, toggleAutoAdvance, autoAdvanceSpeed]);

  if (!isDemoActive || !currentScenario || isMobile || showIntro) return null;

  const persona = currentStep ? currentScenario.personas[currentStep.actAsSlot] : null;

  return (
    <div className="fixed top-0 left-0 right-0 z-[9998] bg-gradient-to-r from-violet-600 to-indigo-600 text-white shadow-lg">
      <div className="flex items-center justify-between px-4 py-2 gap-3">
        {/* Left: Scenario name + persona */}
        <div className="flex items-center gap-3 min-w-0">
          <span className="text-xs font-bold uppercase tracking-wider opacity-75">Demo</span>
          <span className="text-sm font-semibold truncate">{currentScenario.name}</span>
          {persona && (
            <div className="flex items-center gap-1.5 bg-white/20 rounded-full px-2 py-0.5">
              {persona.avatarUrl && (
                <img src={persona.avatarUrl} alt={persona.name} className="w-5 h-5 rounded-full object-cover" />
              )}
              <span className="text-xs font-medium truncate max-w-[120px]">{persona.name}</span>
            </div>
          )}
        </div>

        {/* Center: Navigation */}
        <div className="flex items-center gap-2">
          <Button
            variant="ghost" size="sm"
            className="text-white hover:bg-white/20 h-7 w-7 p-0"
            onClick={prevStep}
            disabled={currentStepIndex <= 0 || isExecutingAction}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          <div className="relative flex items-center gap-2 min-w-[120px]" title={`${Math.round(((currentStepIndex + 1) / totalSteps) * 100)}% complete`}>
            <div className="relative h-1.5 w-16 rounded-full bg-white/20 overflow-hidden group cursor-default">
              <div
                className="absolute inset-y-0 left-0 rounded-full bg-white/90 transition-all duration-300 ease-out"
                style={{ width: `${((currentStepIndex + 1) / totalSteps) * 100}%` }}
              />
              <span className="absolute inset-0 flex items-center justify-center text-[9px] font-mono font-bold text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-sm">
                {Math.round(((currentStepIndex + 1) / totalSteps) * 100)}%
              </span>
            </div>
            <button
              className="text-xs font-mono tabular-nums text-center whitespace-nowrap hover:bg-white/20 rounded px-1.5 py-0.5 transition-colors flex items-center gap-1"
              onClick={() => setShowJumpMenu(prev => !prev)}
              disabled={isExecutingAction}
              title="Jump to step (click to open)"
            >
              {currentStepIndex + 1} / {totalSteps}
              <List className="h-3 w-3 opacity-60" />
            </button>
          </div>

          <Button
            variant="ghost" size="sm"
            className="text-white hover:bg-white/20 h-7 w-7 p-0"
            onClick={nextStep}
            disabled={currentStepIndex >= totalSteps - 1 || isExecutingAction}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>

          <div className="h-4 w-px bg-white/20 mx-0.5" />

          {/* Auto-advance toggle with countdown ring */}
          <div className="relative flex items-center justify-center" style={{ width: 32, height: 32 }}>
            {isAutoAdvancing && !isPaused && !isExecutingAction && (
              <CountdownRing
                durationMs={currentIntervalMs}
                paused={isPaused || isExecutingAction}
                stepKey={currentStepIndex}
              />
            )}
            <Button
              variant="ghost" size="sm"
              className={`text-white h-7 w-7 p-0 ${isAutoAdvancing ? 'bg-white/30 hover:bg-white/40' : 'hover:bg-white/20'}`}
              onClick={toggleAutoAdvance}
              title={isAutoAdvancing ? 'Stop auto-advance (A)' : 'Start auto-advance (A)'}
            >
              {isAutoAdvancing ? (
                <Pause className="h-3.5 w-3.5" />
              ) : (
                <SkipForward className="h-3.5 w-3.5" />
              )}
            </Button>
          </div>

          {/* Speed selector (visible when auto-advancing) */}
          {isAutoAdvancing && (
            <Button
              variant="ghost" size="sm"
              className="text-white hover:bg-white/20 h-7 px-2 text-xs font-mono tabular-nums"
              onClick={cycleSpeed}
              title="Cycle speed (S)"
            >
              <FastForward className="h-3 w-3 mr-1" />
              {SPEED_LABELS[autoAdvanceSpeed]}
            </Button>
          )}

          <Button
            variant="ghost" size="sm"
            className="text-white hover:bg-white/20 h-7 w-7 p-0"
            onClick={togglePause}
            title={isPaused ? 'Resume (P)' : 'Pause (P)'}
          >
            {isPaused ? <Play className="h-3.5 w-3.5" /> : <Pause className="h-3.5 w-3.5" />}
          </Button>
        </div>

        {/* Right: Shortcuts hint + Exit */}
        <div className="flex items-center gap-2">
          <Button
            variant="ghost" size="sm"
            className={`text-white h-7 w-7 p-0 ${showShortcuts ? 'bg-white/30' : 'hover:bg-white/20 opacity-60'}`}
            onClick={() => setShowShortcuts(prev => !prev)}
            title="Keyboard shortcuts (?)"
          >
            <Keyboard className="h-4 w-4" />
          </Button>

          <Button
            variant="ghost" size="sm"
            className="text-white hover:bg-white/20 h-7 px-2 text-xs"
            onClick={exitDemo}
          >
            <X className="h-3.5 w-3.5 mr-1" />
            Exit
          </Button>
        </div>
      </div>

      {/* Bottom accent line — animate when auto-advancing */}
      <div className="h-[2px] bg-white/10 overflow-hidden">
        {isAutoAdvancing && !isPaused && (
          <div className="h-full bg-white/50 animate-pulse" />
        )}
      </div>

      {/* Keyboard shortcuts overlay */}
      {showShortcuts && (
        <div
          className="absolute top-full right-4 mt-2 w-64 rounded-lg bg-popover text-popover-foreground border border-border shadow-xl p-4 animate-in fade-in slide-in-from-top-2 duration-200 z-[9999]"
          onClick={() => setShowShortcuts(false)}
        >
          <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3">Keyboard Shortcuts</h4>
          <div className="space-y-2 text-sm">
            {([
              ['→  /  Space', 'Next step'],
              ['←', 'Previous step'],
              ['Home', 'First step'],
              ['End', 'Last step'],
              ['P', 'Pause / Resume'],
              ['A', 'Toggle auto-advance'],
              ['S', 'Cycle speed'],
              ['?', 'Toggle this panel'],
              ['Esc ×2', 'Exit demo'],
            ] as const).map(([key, desc]) => (
              <div key={key} className="flex items-center justify-between gap-3">
                <kbd className="inline-flex items-center rounded border border-border bg-muted px-1.5 py-0.5 text-[11px] font-mono text-muted-foreground">{key}</kbd>
                <span className="text-xs text-foreground">{desc}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Jump to step dropdown */}
      {showJumpMenu && currentScenario && (
        <div
          ref={jumpMenuRef}
          className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-80 rounded-lg bg-popover text-popover-foreground border border-border shadow-xl animate-in fade-in slide-in-from-top-2 duration-200 z-[9999]"
        >
          <div className="px-3 py-2 border-b border-border">
            <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Chapters</h4>
          </div>
          <div className="max-h-64 overflow-y-auto overscroll-contain py-1" onWheel={(e) => e.stopPropagation()}>
              {currentScenario.storyPoints.map((sp, i) => {
                const spPersona = currentScenario.personas[sp.actAsSlot];
                return (
                  <button
                    key={i}
                    className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 hover:bg-accent hover:text-accent-foreground transition-colors ${
                      i === currentStepIndex ? 'bg-accent/50 text-accent-foreground font-semibold' : 'text-foreground'
                    }`}
                    disabled={isExecutingAction}
                    onClick={() => {
                      jumpToStep(i);
                      setShowJumpMenu(false);
                    }}
                  >
                    <span className="font-mono tabular-nums text-muted-foreground w-6 text-right shrink-0">{i + 1}</span>
                    <span className="truncate flex-1">{sp.title || sp.narrative.replace(/\*\*/g, '').slice(0, 60)}</span>
                    {spPersona && (
                      <span className="text-[10px] text-muted-foreground shrink-0">{spPersona.name} · <span className="capitalize">{spPersona.role}</span></span>
                    )}
                  </button>
                );
              })}
          </div>
        </div>
      )}
    </div>
  );
}